import Swal from "sweetalert2";
import Graphs from "./components/Graphs";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import Accounts from "./components/Accounts";
import { useLocation } from "react-router-dom";
import "../../assets/css/affiliateProgram.css";
import { useNavigate, Link } from "react-router-dom";
import MembersCount from "./components/MembersCount";
import MembersStats from "./components/MembersStats";
import { toastrOnTopCenter } from "../../utils/toastr";
import { getCurrentUser } from "../../redux/selectors";
import { globalImages } from "../../utils/staticImages";
import { LEVEL_ADMIN } from "../../utils/userPermissions";
import React, { useRef, useEffect, useState } from "react";
import DateRangeFilter from "./components/DateRangeFilter";
import { usePermissions } from "../../context/permissionsContext";
import AuthenticatedLayout from "../../layouts/AuthenticatedLayout";
import DotPulseBackdrop from "../shared/components/DotPulseBackdrop";
import TermsConditionsModal from "./components/TermsConditionsModal";
import {
  fetchAffiliateStats,
  fetchAffiliateUsers,
  updatePayoutStatus,
} from "../../api/Affiliate";
import { promoteImages } from "../../utils/portalImages";

function AffiliateProgram() {
  const {
    reset,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: {},
  });

  const statsRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [endDate, setEndDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [payoutsPaused, setPayoutsPaused] = useState();
  const [paginationData, setPaginationData] = useState({});
  const [affiliateStats, setAffiliateStats] = useState({});
  const [affiliateUsers, setAffiliateUsers] = useState([]);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const currentUser = useSelector((state) => getCurrentUser(state));
  const [showDateFilterModal, setShowDateFilterModal] = useState(false);

  useEffect(() => {
    setShowTermsModal(true);
    fetchAffiliateStatsData();
    fetchAffiliateUsersData();
  }, [payoutsPaused]);

  useEffect(() => {
    setTimeout(() => {
      if (location.search.includes("leads") && statsRef.current) {
        statsRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 1000);
  }, [location.search]);

  const fetchAffiliateStatsData = async (filter = {}, url = null) => {
    setIsLoading(true);
    setError(null);
    try {
      const statsResponse = await fetchAffiliateUsers(filter, url);
      setAffiliateStats(statsResponse.data);
      setPaginationData(statsResponse.data.affiliated_users);
      reset((prevData) => ({ ...prevData, ...statsResponse }));
    } catch (error) {
      console.error("Error fetching stats:", error);
      toastrOnTopCenter(
        "Failed to fetch stats. Please try again later.",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAffiliateUsersData = async () => {
    const affiliate_id = null;
    setIsLoading(true);
    setError(null);
    try {
      const usersResponse = await fetchAffiliateStats(affiliate_id);
      setAffiliateUsers(usersResponse.data);
      setPayoutsPaused(usersResponse.data?.is_pause);

      reset((prevData) => ({ ...prevData, users: usersResponse.users }));
    } catch (error) {
      console.error("Error fetching users:", error);
      toastrOnTopCenter(
        "Failed to fetch users. Please try again later.",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (url) => {
    if (!url) return;
    fetchAffiliateStatsData(
      { query: searchQuery || null, from_date: startDate, to_date: endDate },
      url
    );
  };

  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleQuerySubmit = (e) => {
    e.preventDefault();
    fetchAffiliateStatsData({
      query: searchQuery || null,
      from_date: startDate,
      to_date: endDate,
    });
    fetchAffiliateUsersData({
      query: searchQuery || null,
      from_date: startDate,
      to_date: endDate,
    });
  };

  const clearFilter = () => {
    setEndDate("");
    setStartDate("");
    setSearchQuery("");
    fetchAffiliateStatsData({ query: null });
    fetchAffiliateUsersData({ query: null });
  };

  const handleDateFilterChange = (start, end) => {
    setEndDate(end);
    setStartDate(start);
    fetchAffiliateStatsData({
      query: searchQuery || null,
      from_date: start,
      to_date: end,
    });
    fetchAffiliateUsersData({
      query: searchQuery || null,
      from_date: start,
      to_date: end,
    });
  };

  const toggleDateFilterModal = () => {
    setShowDateFilterModal(false);
    document.body.classList.remove("modal-open");
  };

  const toggleTermsModal = () => {
    setShowTermsModal(false);
    document.body.classList.remove("modal-open");
  };

  const handleTogglePayouts = async () => {
    if (!payoutsPaused) {
      Swal.fire({
        title: "Are you sure you want to pause payouts?",
        text: "Please enter a reason for pausing payouts:",
        input: "text",
        inputValue:
          "Payouts are temporarily paused by the admin. Please try again later.",
        icon: "warning",
        showCancelButton: true,
        buttonsStyling: false,
        customClass: {
          confirmButton: "btn btn-danger",
          cancelButton: "btn btn-secondary",
        },
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const response = await updatePayoutStatus({
              is_paused: true,
              message: result.value || null,
            });
            setPayoutsPaused(true);
            toastrOnTopCenter(response.message, "success");
          } catch (error) {
            console.error("Error updating payout status:", error);
          }
        }
      });
    } else {
      Swal.fire({
        title: "Are you sure you want to resume payouts?",
        icon: "warning",
        showCancelButton: true,
        buttonsStyling: false,
        customClass: {
          confirmButton: "btn btn-success",
          cancelButton: "btn btn-secondary",
        },
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const response = await updatePayoutStatus({
              is_paused: false,
              message: null,
            });
            setPayoutsPaused(false);
            toastrOnTopCenter(response.message, "success");
          } catch (error) {
            console.error("Error updating payout status:", error);
          }
        }
      });
    }
  };

  return (
    <>
      <div className="card">
        <div
          className="card"
          style={{
            backgroundSize: "cover",
            backgroundPosition: "center",
            margin: "29px",
            backgroundImage: `url(${globalImages.affiliate_backgound_image})`,
          }}
        >
          <span className="text-default text-xl font-weight-bold pl-4 pt-4"></span>
          <div className="row d-space-between pl-4 ml-0 pr-3">
            <Accounts
              users={affiliateUsers}
              payoutsPaused={payoutsPaused}
              setPayoutsPaused={setPayoutsPaused}
            />
            <MembersCount formData={affiliateStats} />
          </div>
        </div>
        <div className="col-md-12 col-12 d-flex align-items-center px-5 mx-1 mb-4">
          {hasPermission(LEVEL_ADMIN) ? (
            <div className="d-space-between w-100">
              <div>
                <span className="mr-2">Pause Payouts</span>
                <label className="switch mb-0">
                  <input
                    type="checkbox"
                    checked={!payoutsPaused}
                    onChange={handleTogglePayouts}
                  />
                  <span className="sliderr round"></span>
                </label>
                <span className="ml-2">Resume Payouts</span>
              </div>
              <button
                className="btn btn-default px-5 py-2"
                onClick={() => navigate("/admin/w9-form-requests")}
              >
                User Requests
              </button>
            </div>
          ) : (
            <>
              <Link
                className="d-center ml-3"
                to="/affiliate-program/initiate-withdraw"
                style={{ borderLeft: "2px solid #f0f2f5" }}
              >
                <img
                  className="pl-2"
                  alt="Initiate Withdraw"
                  src={promoteImages.initiate_earnings}
                />
                <span className="text-default pl-2">Initiate Withdraw</span>
              </Link>
              <Link
                className="d-center ml-3"
                to="/affiliate-program/income-history"
                style={{ borderLeft: "2px solid #f0f2f5" }}
              >
                <img
                  className="pl-2"
                  src={promoteImages.income_history}
                  alt="Income History"
                />
                <span className="text-default pl-2">Income History</span>
              </Link>
            </>
          )}
        </div>
        <div className="row justify-content-center px-5">
          <Graphs graphData={affiliateUsers} />
        </div>
        <div className="row justify-content-center">
          <MembersStats
            errors={errors}
            statsRef={statsRef}
            formData={affiliateStats}
            searchQuery={searchQuery}
            onClearFilter={clearFilter}
            onPageChange={handlePageChange}
            paginationData={paginationData}
            onInputChange={handleInputChange}
            onQuerySubmit={handleQuerySubmit}
            setShowDateFilterModal={setShowDateFilterModal}
          />
        </div>
        {showTermsModal &&
          !currentUser.w9_checked &&
          !hasPermission(LEVEL_ADMIN) && (
            <TermsConditionsModal toggleTermsModal={toggleTermsModal} />
          )}
        {showDateFilterModal && (
          <DateRangeFilter
            onDateChange={handleDateFilterChange}
            toggleDateFilterModal={toggleDateFilterModal}
          />
        )}
        {isLoading && <DotPulseBackdrop />}
      </div>
    </>
  );
}

export default AuthenticatedLayout(AffiliateProgram);
